import { KEYWORDS, Pos, Span, Token, TokenType } from "./token.js";

export class LexError extends Error {
  constructor(message: string, public span: Span) {
    super(message);
    this.name = "LexError";
  }
}

const SINGLE: Record<string, TokenType> = {
  "(": "(",
  ")": ")",
  "{": "{",
  "}": "}",
  "[": "[",
  "]": "]",
  ",": ",",
  ":": ":",
  ";": ";",
  "%": "%",
  "<": "<",
  ">": ">",
};

/** Convert a program's source text into a list of tokens ending with `eof`. */
export function lex(src: string): Token[] {
  return lexImpl(src, null);
}

/** Tokenize tolerantly: never throws — malformed input is skipped and reported
 *  in `errors` so the parser can still build a (partial) tree from it. */
export function lexRecover(src: string): { tokens: Token[]; errors: LexError[] } {
  const errors: LexError[] = [];
  return { tokens: lexImpl(src, errors), errors };
}

/** Shared tokenizer core. When `errors` is null, the first problem throws
 *  (strict mode); otherwise problems are collected and the lexer recovers. */
function lexImpl(src: string, errors: LexError[] | null): Token[] {
  const tokens: Token[] = [];
  let offset = 0;
  let line = 1;
  let col = 1;

  const pos = (): Pos => ({ offset, line, col });

  /** Report a lex error: throw in strict mode, collect and continue otherwise. */
  const fail = (message: string, span: Span): void => {
    if (errors) errors.push(new LexError(message, span));
    else throw new LexError(message, span);
  };

  const advance = (): string => {
    const ch = src[offset]!;
    offset++;
    if (ch === "\n") {
      line++;
      col = 1;
    } else {
      col++;
    }
    return ch;
  };

  const peek = (ahead = 0): string => src[offset + ahead] ?? "";

  const push = (type: TokenType, text: string, start: Pos, value?: number | string) => {
    tokens.push({ type, text, value, span: { start, end: pos() } });
  };

  while (offset < src.length) {
    const ch = peek();

    // whitespace
    if (ch === " " || ch === "\t" || ch === "\r" || ch === "\n") {
      advance();
      continue;
    }

    // comments: // and #
    if ((ch === "/" && peek(1) === "/") || ch === "#") {
      while (offset < src.length && peek() !== "\n") advance();
      continue;
    }

    const start = pos();

    // numbers
    if (isDigit(ch)) {
      let text = "";
      while (isDigit(peek())) text += advance();
      let isFloat = false;
      if (peek() === "." && isDigit(peek(1))) {
        isFloat = true;
        text += advance(); // .
        while (isDigit(peek())) text += advance();
      }
      if (isFloat) {
        push("float", text, start, parseFloat(text));
      } else {
        push("int", text, start, parseInt(text, 10));
      }
      continue;
    }

    // identifiers / keywords
    if (isIdentStart(ch)) {
      let text = "";
      while (isIdentPart(peek())) text += advance();
      const kw = KEYWORDS[text];
      push(kw ?? "ident", text, start);
      continue;
    }

    // strings
    if (ch === '"') {
      advance(); // opening quote
      let value = "";
      while (offset < src.length && peek() !== '"' && peek() !== "\n") {
        const c = advance();
        if (c === "\\") {
          const esc = advance();
          const decoded = unescape(esc);
          if (decoded === null) {
            fail(`invalid escape sequence \\${esc}`, { start, end: pos() });
            value += esc; // recovery: keep the escaped character literally
          } else {
            value += decoded;
          }
        } else {
          value += c;
        }
      }
      if (peek() !== '"') {
        // Unterminated (hit newline or EOF): report and emit what we have so the
        // parser can keep going past the bad line.
        fail("unterminated string literal", { start, end: pos() });
        push("string", src.slice(start.offset, offset), start, value);
        continue;
      }
      advance(); // closing quote
      push("string", src.slice(start.offset, offset), start, value);
      continue;
    }

    // char literals: a single code point between single quotes, e.g. `'a'`,
    // `'\n'`, `'0'`. The `value` is the code point (a number), like an `int`.
    if (ch === "'") {
      advance(); // opening quote
      let value = "";
      while (offset < src.length && peek() !== "'" && peek() !== "\n") {
        const c = advance();
        if (c === "\\") {
          const esc = advance();
          const decoded = unescape(esc);
          if (decoded === null) {
            fail(`invalid escape sequence \\${esc}`, { start, end: pos() });
            value += esc; // recovery: keep the escaped character literally
          } else {
            value += decoded;
          }
        } else {
          value += c;
        }
      }
      if (peek() !== "'") {
        fail("unterminated character literal", { start, end: pos() });
        push("char", src.slice(start.offset, offset), start, 0);
        continue;
      }
      advance(); // closing quote
      // A char is exactly one Unicode code point. `[...value]` splits by code
      // point (not UTF-16 unit), so an astral character counts as one.
      const points = [...value];
      if (points.length !== 1) {
        fail(
          `a character literal must contain exactly one character, got ${points.length}`,
          { start, end: pos() }
        );
      }
      push("char", src.slice(start.offset, offset), start, points[0]?.codePointAt(0) ?? 0);
      continue;
    }

    // multi-char operators
    const two = ch + peek(1);
    const twoChar = TWO_CHAR[two];
    if (twoChar) {
      advance();
      advance();
      push(twoChar, two, start);
      continue;
    }

    // single-char operators
    if (ch === "=") {
      advance();
      push("=", "=", start);
      continue;
    }
    if (ch === "+" || ch === "-" || ch === "*" || ch === "/") {
      advance();
      push(ch as TokenType, ch, start);
      continue;
    }
    if (ch === ".") {
      advance();
      push(".", ".", start);
      continue;
    }
    if (ch === "!") {
      advance();
      push("!", "!", start);
      continue;
    }
    const single = SINGLE[ch];
    if (single) {
      advance();
      push(single, ch, start);
      continue;
    }

    fail(`unexpected character ${JSON.stringify(ch)}`, {
      start,
      end: { ...start, offset: start.offset + 1, col: start.col + 1 },
    });
    advance(); // recovery: skip the offending character and carry on
  }

  tokens.push({ type: "eof", text: "", span: { start: pos(), end: pos() } });
  return tokens;
}

const TWO_CHAR: Record<string, TokenType> = {
  "=>": "=>",
  "->": "->",
  "+=": "+=",
  "-=": "-=",
  "*=": "*=",
  "/=": "/=",
  "==": "==",
  "!=": "!=",
  "<=": "<=",
  ">=": ">=",
  "&&": "&&",
  "||": "||",
  "..": "..",
};

function isDigit(ch: string): boolean {
  return ch >= "0" && ch <= "9";
}

function isIdentStart(ch: string): boolean {
  return (ch >= "a" && ch <= "z") || (ch >= "A" && ch <= "Z") || ch === "_";
}

function isIdentPart(ch: string): boolean {
  return isIdentStart(ch) || isDigit(ch);
}

/** Decode an escape sequence, or null if it is invalid (the caller reports it). */
function unescape(esc: string): string | null {
  switch (esc) {
    case "n":
      return "\n";
    case "t":
      return "\t";
    case "r":
      return "\r";
    case "\\":
      return "\\";
    case '"':
      return '"';
    case "'":
      return "'";
    case "0":
      return "\0";
    default:
      return null;
  }
}
